import { create } from 'zustand';

export interface GiftState {
  id: string;
  x: number; // percentage position
  y: number; // percentage position
  width: number; // original width in pixels
  height: number; // original height in pixels
  burned: boolean;
  burning: boolean;
}

interface HolidayStore {
  // Fireplace state
  fireplaceOn: boolean;
  setFireplaceOn: (on: boolean) => void;
  toggleFireplace: () => void;

  // Gifts state
  gifts: GiftState[];
  initializeGifts: (positions: Array<{ id: string; x: number; y: number; width: number; height: number }>) => void;
  startBurningGift: (id: string) => void;
  finishBurningGift: (id: string) => void;

  // Computed helpers
  allGiftsBurned: () => boolean;
  burnedCount: () => number;
  totalGifts: () => number;

  // Decoration animation
  activeDecoration: string | null;
  triggerDecoration: (id: string) => void;
  clearDecoration: () => void;

  // Selected gift state (Step 1: user clicks a gift to select it)
  selectedGiftId: string | null;
  setSelectedGiftId: (id: string | null) => void;

  // Wrapped verification modal (Step 2: verify wrapped tweet)
  showWrappedModal: boolean;
  setShowWrappedModal: (show: boolean) => void;
  wrappedVerified: boolean;
  setWrappedVerified: (verified: boolean) => void;
  verifyingWrapped: boolean;
  setVerifyingWrapped: (verifying: boolean) => void;

  // Phrase reveal state (Step 3: shown after burning the verified gift)
  showPhraseReveal: boolean;
  setShowPhraseReveal: (show: boolean) => void;

  // Reply tweet verification state (Step 4: user verifies their reply tweet)
  showTweetModal: boolean;
  setShowTweetModal: (show: boolean) => void;
  tweetVerified: boolean;
  setTweetVerified: (verified: boolean) => void;
  verifyingTweet: boolean;
  setVerifyingTweet: (verifying: boolean) => void;

  // Ritual complete modal (shown after tweet verification)
  showRitualComplete: boolean;
  setShowRitualComplete: (show: boolean) => void;

  // Reward state
  showReward: boolean;
  reward: { type: string; amount: number } | null;
  setReward: (reward: { type: string; amount: number } | null) => void;
  setShowReward: (show: boolean) => void;

  // Dragging state
  draggingGiftId: string | null;
  setDraggingGiftId: (id: string | null) => void;

  // Reset for remount
  reset: () => void;
}

const initialState = {
  fireplaceOn: false,
  gifts: [] as GiftState[],
  activeDecoration: null,
  // Selected gift state
  selectedGiftId: null as string | null,
  // Wrapped verification state
  showWrappedModal: false,
  wrappedVerified: false,
  verifyingWrapped: false,
  // Phrase reveal state
  showPhraseReveal: false,
  // Reply tweet verification state
  showTweetModal: false,
  tweetVerified: false,
  verifyingTweet: false,
  // Ritual complete modal
  showRitualComplete: false,
  showReward: false,
  reward: null,
  draggingGiftId: null,
};

export const useHolidayStore = create<HolidayStore>((set, get) => ({
  ...initialState,

  setFireplaceOn: (on) => set({ fireplaceOn: on }),
  toggleFireplace: () => set((state) => ({ fireplaceOn: !state.fireplaceOn })),

  initializeGifts: (positions) => set({
    gifts: positions.map((p) => ({
      ...p,
      burned: false,
      burning: false,
    })),
  }),

  startBurningGift: (id) => set((state) => ({
    gifts: state.gifts.map((g) =>
      g.id === id ? { ...g, burning: true } : g
    ),
    draggingGiftId: null,
  })),

  finishBurningGift: (id) => {
    set((state) => ({
      gifts: state.gifts.map((g) =>
        g.id === id ? { ...g, burning: false, burned: true } : g
      ),
    }));

    // Show phrase reveal after burning the verified gift
    const currentState = get();
    if (currentState.wrappedVerified && !currentState.showPhraseReveal && !currentState.tweetVerified) {
      setTimeout(() => {
        set({ showPhraseReveal: true });
      }, 300);
    }
  },

  allGiftsBurned: () => {
    const { gifts } = get();
    return gifts.length > 0 && gifts.every((g) => g.burned);
  },

  burnedCount: () => get().gifts.filter((g) => g.burned).length,

  totalGifts: () => get().gifts.length,

  triggerDecoration: (id) => {
    set({ activeDecoration: id });
    // Auto-clear after animation
    setTimeout(() => {
      set((state) => (state.activeDecoration === id ? { activeDecoration: null } : state));
    }, 500);
  },

  clearDecoration: () => set({ activeDecoration: null }),

  // Selected gift functions
  setSelectedGiftId: (id) => set({ selectedGiftId: id }),

  // Wrapped verification functions
  setShowWrappedModal: (show) => set({ showWrappedModal: show }),
  setWrappedVerified: (verified) => set({ wrappedVerified: verified }),
  setVerifyingWrapped: (verifying) => set({ verifyingWrapped: verifying }),

  // Phrase reveal
  setShowPhraseReveal: (show) => set({ showPhraseReveal: show }),

  // Reply tweet verification
  setShowTweetModal: (show) => set({ showTweetModal: show }),
  setTweetVerified: (verified) => {
    set({ tweetVerified: verified });
    // Show ritual complete modal when verified
    if (verified) {
      set({ showRitualComplete: true });
    }
  },
  setVerifyingTweet: (verifying) => set({ verifyingTweet: verifying }),

  // Ritual complete modal
  setShowRitualComplete: (show) => set({ showRitualComplete: show }),

  setReward: (reward) => set({ reward }),
  setShowReward: (show) => set({ showReward: show }),

  setDraggingGiftId: (id) => set({ draggingGiftId: id }),

  reset: () => set(initialState),
}));
